import toast from 'react-hot-toast'
// NPM Imports
import { useState, useEffect } from 'react'
import { IQuoteContext } from './QuoteContext'
import { removePromoDiscountNotInCurrentBook } from '../js/validate'
import {
   timeClockDetail,
   serviceDetails,
   EINIntegrationSelection,
   //@ts-ignore
} from '../common/types/types.d.ts'
import { DealDeskDiscount } from '../types/types'
import { getBusinessToday, isBeforeBusinessToday } from '../utils/businessDate'

/////////////////////////////////////
// Set host url based on the environment.
/////////////////////////////////////
const host_url = process.env.REACT_APP_HOST_URL
interface SaveOpportunityParameters {
   state: IQuoteContext | null
   quote_type?: string
   expiration_date?: string
   core_selected?: string
   time_product_selected?: string
   core_count?: number
   time_tracking_count?: number
   has_time_clocks?: boolean
   time_clock_details?: timeClockDetail[]
   us_employees?: boolean
   is_ats_only?: boolean
   benefits_administration_count?: number
   payroll_count?: number
   states_count?: number
   imp_type?: string
   ben_admin_imp_type?: string
   payroll_imp_type?: string
   bundle_discount?: boolean
   nonprofit_discount?: boolean
   term_length?: number
   prepay_length?: number
   annual_prepayment_discount?: string
   multi_entity_discount?: boolean
   hr_promotion_monthly_discount?: boolean
   hr_dealdesk_monthly_discount?: boolean
   reseller_discount?: number
   quickstart_training_discount?: boolean
   promotion_imp_discount?: boolean
   hr_dealdesk_imp_discount?: boolean
   payroll_dealdesk_imp_discount?: boolean
   second_ein_discount?: boolean
   group_imp_discount?: boolean
   additional_storage_count?: number
   additional_job_count?: number
   historical_employee_import?: boolean
   marketplace_listing?: boolean
   sponsorship?: boolean
   paid_service?: number
   quote_notes?: string
   test_account?: boolean
   activation_date?: string
   payroll_first_pay_date?: string | null
   difference_quote?: boolean
   is_co_term?: boolean
   account_snowflake?: string
   core_country_count?: number
   promotional_discounts?: string
   next_invoice_date?: string
   currency_code?: string
   account_balance?: number
   monthly_tax?: number
   ein_names?: string[]
   ein_ee_counts?: number[]
   ein_state_counts?: number[]
   ein_imp_types?: string[]
   ein_count?: number
   ein_integrations?: EINIntegrationSelection[][]
   account_status?: string
   record_type?: string
   deal_desk_locks?: Set<string>
   professional_service_details?: {
      serviceCode: string
      count: number
      [key: string]: string | number
   }[]
   consulting_service_details?: {
      serviceCode: string
      count: number
      [key: string]: string | number
   }[]
   partner_account?: string
   region?: string
   prepayment_activation_use_difference?: boolean
   dynamic_products?: Map<string, number>
   stage?: string
   company_id?: string
   deal_desk_discounts?: DealDeskDiscount[]
   hasUnlimitedDatePermission?: boolean
}

/**
 * Saves an opportunity by processing the provided quote context and related parameters,
 * performing validations (such as expiration date and pricebook checks), cleaning up
 * EIN integration data, and then sending the data to the backend.
 * Except for state, all other properties are optional.
 * If provided, they will overwrite whatever is in the state context in order to mitigate race conditions.
 *
 * @param {Object} options - The opportunity data.
 * @param {IQuoteContext|null} options.state - The quote context object; must not be null.
 * @param {string} [options.quote_type=''] - The type of quote.
 * @param {string} [options.expiration_date=''] - The expiration date for the opportunity.
 * @param {string} [options.core_selected=''] - The identifier for the core product selected.
 * @param {number} [options.core_count=0] - The core count value.
 * @param {number} [options.time_tracking_count=0] - Time tracking count.
 * @param {boolean} [options.has_time_clocks=false] - Whether time clocks are selected.
 * @param {timeClockDetail[]} [options.time_clock_details=[]] - Array of time clock detail objects.
 * @param {boolean} [options.us_employees=false] - Indicates if there are US employees.
 * @param {number} [options.benefits_administration_count=0] - Count for benefits administration.
 * @param {number} [options.payroll_count=0] - Payroll count.
 * @param {number} [options.states_count=0] - Count of payroll states.
 * @param {string} [options.imp_type=''] - The implementation type.
 * @param {string} [options.ben_admin_imp_type=''] - Benefits administration implementation type.
 * @param {string} [options.payroll_imp_type=''] - Payroll implementation type.
 * @param {boolean} [options.bundle_discount=false] - Flag for bundle discount.
 * @param {boolean} [options.nonprofit_discount=false] - Flag for nonprofit discount.
 * @param {number} [options.term_length=0] - Contract Term length in months.
 * @param {number} [options.prepay_length=1] - Prepay length in months (decoupled from term).
 * @param {string} [options.annual_prepayment_discount=''] - Annual prepayment discount value.
 * @param {boolean} [options.multi_entity_discount=false] - Flag for multi-entity discount.
 * @param {boolean} [options.hr_promotion_monthly_discount=false] - Flag for HR promotion monthly discount.
 * @param {boolean} [options.hr_dealdesk_monthly_discount=false] - Flag for HR deal desk monthly discount.
 * @param {number} [options.reseller_discount=0] - Reseller discount value.
 * @param {boolean} [options.quickstart_training_discount=false] - Flag for quickstart training discount.
 * @param {boolean} [options.promotion_imp_discount=false] - Flag for promotion implementation discount.
 * @param {boolean} [options.hr_dealdesk_imp_discount=false] - Flag for HR deal desk implementation discount.
 * @param {boolean} [options.payroll_dealdesk_imp_discount=false] - Flag for payroll deal desk implementation discount.
 * @param {boolean} [options.second_ein_discount=false] - Flag for second EIN discount.
 * @param {boolean} [options.group_imp_discount=false] - Flag for group implementation discount.
 * @param {number} [options.additional_storage_count=0] - Additional storage count.
 * @param {number} [options.additional_job_count=0] - Additional job count.
 * @param {boolean} [options.historical_employee_import=false] - Flag for historical employee import.
 * @param {boolean} [options.marketplace_listing=false] - Flag for marketplace listing.
 * @param {boolean} [options.sponsorship=false] - Flag for sponsorship.
 * @param {number} [options.paid_service=0] - Amount for paid service.
 * @param {string} [options.quote_notes=''] - Quote notes (will be encoded as Base64).
 * @param {boolean} [options.test_account=false] - Flag for a test account.
 * @param {string} [options.activation_date=''] - Activation date.
 * @param {boolean} [options.difference_quote=false] - Flag indicating a difference quote.
 * @param {string} [options.account_snowflake=''] - Account snowflake object.
 * @param {number} [options.core_country_count=0] - Count for core countries.
 * @param {string} [options.promotional_discounts=''] - Promotional discounts (string or JSON structure).
 * @param {string} [options.next_invoice_date=''] - Next invoice date.
 * @param {string} [options.currency_code=''] - Currency code.
 * @param {number} [options.account_balance=0] - Account balance.
 * @param {number} [options.monthly_tax=0] - Monthly tax amount.
 * @param {string[]} [options.ein_names=[]] - Array of EIN names.
 * @param {number[]} [options.ein_ee_counts=[]] - Array of EIN employee counts.
 * @param {number[]} [options.ein_state_counts=[]] - Array of EIN state counts.
 * @param {string[]} [options.ein_imp_types=[]] - Array of EIN implementation types.
 * @param {number} [options.ein_count=0] - Total count of EINs.
 * @param {EINIntegrationSelection[]} [options.ein_integrations=[]] - Array of EIN integration selections.
 * @param {string} [options.account_status=''] - Account status.
 * @param {string} [options.record_type=''] - Record type.
 * @param {Set<string>} [options.deal_desk_locks=new Set<string>()] - Set of deal desk locks.
 * @param {{ serviceCode: string; count: number; [key: string]: string | number }[]} [options.professional_service_details=[]] - Array of selected professional services.
 * @param {{ serviceCode: string; count: number; [key: string]: string | number }[]} [options.consulting_service_details=[]] - Array of selected consulting services.
 * @param {boolean} [options.prepayment_activation_use_difference=false] - Flag for prepayment activation using difference amount for the entire term.
 * @param {string} [options.stage=''] - Stage of the opportunity.
 * @param {string} [options.company_id=''] - ID of the company.
 * @param {DealDeskDiscount[]} [options.deal_desk_discounts=[]] - Array of deal desk discount records.
 *
 * @returns {Promise<Response>} Returns a promise that resolves to the backend response.
 *
 * @throws {Error} If the provided state is null.
 */
export async function saveOpportunity({
   state,
   is_co_term,
   quote_type = '',
   expiration_date = '',
   core_selected = '',
   time_product_selected = '',
   core_count = 0,
   time_tracking_count = 0,
   has_time_clocks = false,
   time_clock_details = [],
   us_employees = false,
   is_ats_only = false,
   benefits_administration_count = 0,
   payroll_count = 0,
   states_count = 0,
   imp_type = '',
   ben_admin_imp_type = '',
   payroll_imp_type = '',
   bundle_discount = false,
   nonprofit_discount = false,
   term_length = 1,
   prepay_length = 1,
   annual_prepayment_discount = '',
   multi_entity_discount = false,
   hr_promotion_monthly_discount = false,
   hr_dealdesk_monthly_discount = false,
   reseller_discount = 0,
   quickstart_training_discount = false,
   promotion_imp_discount = false,
   hr_dealdesk_imp_discount = false,
   payroll_dealdesk_imp_discount = false,
   second_ein_discount = false,
   group_imp_discount = false,
   additional_storage_count = 0,
   additional_job_count = 0,
   historical_employee_import = false,
   marketplace_listing = false,
   sponsorship = false,
   paid_service = 0,
   quote_notes = '',
   test_account = false,
   activation_date = '',
   payroll_first_pay_date = null,
   difference_quote = false,
   account_snowflake = '',
   core_country_count = 0,
   promotional_discounts = '',
   next_invoice_date = '',
   currency_code = '',
   account_balance = 0,
   monthly_tax = 0,
   ein_names = [],
   ein_ee_counts = [],
   ein_state_counts = [],
   ein_imp_types = [],
   ein_count = 0,
   ein_integrations = [],
   account_status = '',
   record_type = '',
   deal_desk_locks,
   professional_service_details = [],
   consulting_service_details = [],
   partner_account = '',
   region = '',
   prepayment_activation_use_difference,
   dynamic_products = new Map<string, number>(),
   stage = '',
   company_id = '',
   deal_desk_discounts,
   hasUnlimitedDatePermission = false,
}: SaveOpportunityParameters) {
   //This will have an error until we use the typed useContext to create the QuoteContext in App.tsx after porting it to typescript
   if (!state || typeof state !== 'object') {
      throw new Error('Invalid state object provided to saveOpportunity')
   }

   // Server-provided date (NTP, America/Denver); client clock only if unavailable
   const serverToday = state.timeData?.currentDate || getBusinessToday()
   const exp_date_str = expiration_date || state.quoteExpirationDate
   if (exp_date_str && isBeforeBusinessToday(exp_date_str, serverToday)) {
      // Move the expiration 14 days past the business-timezone "today"
      const [by, bm, bd] = serverToday.split('-').map(Number)
      const date = new Date(by, bm - 1, bd + 14)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      state.setQuoteExpirationDate(`${year}-${month}-${day}`)
      toast.error(
         `An expiration date set in the past is not allowed. The expiration date was automatically set to ${month}/${day}/${year}. Please review and try again.`,
         { duration: 5000 },
      )
      return
   }
   // RULE: Activation dates may not be in the past (relative to today in the
   // business timezone, America/Denver). Saving or booking a quote with an
   // activation date earlier than today is not allowed. If a past date is
   // detected, auto-move it to today and block this save so the user can
   // review the corrected date before retrying. UnlimitedActivationDateSelect
   // grants unlimited dates in the FUTURE only — never in the past.
   const act_date_str = activation_date || state.activationDate
   if (act_date_str && !hasUnlimitedDatePermission) {
      if (isBeforeBusinessToday(act_date_str, serverToday)) {
         const todayStr = serverToday.split('T')[0]
         const [t_year, t_month, t_day] = todayStr.split('-')
         state.setActivationDate(todayStr)
         toast.error(
            `An activation date in the past is not allowed. The activation date was automatically set to ${t_month}/${t_day}/${t_year}. Please review and try again.`,
            { duration: 5000 },
         )
         return
      }
   }
   // Checks to see if using retired pricebook. If so, denies save and prompts to switch to valid pricebook
   for (let book in state.pricebooks) {
      if (
         state.pricebooks[book]['value'] == state.pricebookId &&
         'retired' in state.pricebooks[book] &&
         state.pricebooks[book]['retired'] == true
      ) {
         toast.error(
            `Selected pricebook has been retired.\n Please select a current pricebook.`,
            { duration: 5000 },
         )
         return
      }
   }
   //checks the previous pricebook (from the database) against the current one. If they don't match, it clears the values for the products of the old pricebook
   // if(state.prevPricebookId != state.pricebookId) {
   //     let productsToClear = Object.keys(state.prevProductList).filter(productNotInNewList);
   //     //TODO: This is a hack way to do this. Eventually, we should make this all table-driven so we can dynamically filter products
   //     productsToClear.forEach(async productCode => {
   //         if(productCode in productCodeToStateSetter) {
   //             productCodeToStateSetter[productCode]();
   //             productCodeToVar[productCode]["value"] = productCodeToVar[productCode]["default"];
   //         }
   //     });
   // }
   //TODO: This is an OK way to do this, but we should eventually have a master object of product values in an opportunity that includes their default value
   const productCodeToVar = {
      performanceMan: { value: state.productPerformanceEECount, default: 0 },
      testAccount: { value: state.productTestAccount, default: false },
   }
   // Filter out all deal desk discount IDs before saving:
   // - temp-* IDs: temporary placeholders for unsaved discounts
   // - UUID format IDs: real deal desk discounts from backend
   // The server automatically adds deal desk discount IDs to promotional_discounts
   // when loading the opportunity, so we don't need to persist them
   const isUUID = (str: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
         str,
      )
   let promo_discounts = Object.fromEntries(
      Object.entries(state.promotionalDiscounts).filter(
         ([key]) => !key.startsWith('temp-') && !isUUID(key),
      ),
   )
   if (state.pricebookId >= 200) {
      state.onProductPerformanceEECountChange(0)
      productCodeToVar['performanceMan']['value'] = 0
   } else {
      //Remove promo discounts not in 100 pricebook
      promo_discounts = removePromoDiscountNotInCurrentBook(
         state.discount_list,
         promo_discounts,
         state.setPromotionalDiscounts,
      )
   }
   // Validate and enforce max discount values for percent discounts
   if (
      promo_discounts &&
      typeof promo_discounts === 'object' &&
      state.discount_list
   ) {
      // Check if numberDiscountValues exists in promotional discounts
      if (
         'numberDiscountValues' in promo_discounts &&
         promo_discounts.numberDiscountValues
      ) {
         const numberDiscountValues =
            promo_discounts.numberDiscountValues as Record<string, number>
         const validatedDiscountValues = { ...numberDiscountValues }

         // Iterate through all discount keys
         Object.keys(numberDiscountValues).forEach((discountKey) => {
            // Check if this discount exists in the discount_list and is a percent discount
            if (
               state.discount_list[discountKey] &&
               state.discount_list[discountKey]['DiscountType'] === 'percent'
            ) {
               // Get the max value (either MaxDiscount or default to 100)
               const maxValue = state.discount_list[discountKey]['MaxDiscount']
                  ? Number(state.discount_list[discountKey]['MaxDiscount'])
                  : 100

               // Enforce the max value
               if (Number(validatedDiscountValues[discountKey]) > maxValue) {
                  validatedDiscountValues[discountKey] = maxValue
               }
            }
         })

         // Clean up discount family selections for zero-value discounts
         const cleanedPromoDiscounts = { ...promo_discounts }
         const cleanedNumberDiscountValues = { ...validatedDiscountValues }

         // First, check for discount families that have been set to "None" and clean up their numberDiscountValues
         // Build a map of discount families to their discount codes
         const familyToDiscounts: Record<string, string[]> = {}
         Object.keys(state.discount_list).forEach((discountKey) => {
            const discount = state.discount_list[discountKey]
            if (discount && discount.DiscountFamily) {
               if (!familyToDiscounts[discount.DiscountFamily]) {
                  familyToDiscounts[discount.DiscountFamily] = []
               }
               familyToDiscounts[discount.DiscountFamily].push(discountKey)
            }
         })

         // Check each family - if the family key is missing (set to None), remove all its discount values.
         // Only applies to discounts managed by DiscountFamilyDropdown, which records its selection
         // under the family-name key. Standalone NumberDiscount inputs (e.g. one-time discount rows)
         // never set the family key, so their values must be preserved when the discount is flagged active.
         Object.keys(familyToDiscounts).forEach((familyName) => {
            const selectedDiscountCode = cleanedPromoDiscounts[familyName]
            if (!selectedDiscountCode) {
               familyToDiscounts[familyName].forEach((discountKey) => {
                  if (cleanedPromoDiscounts[discountKey] === true) return
                  if (cleanedNumberDiscountValues[discountKey] !== undefined) {
                     delete cleanedNumberDiscountValues[discountKey]
                  }
                  if (cleanedPromoDiscounts[discountKey] !== undefined) {
                     delete cleanedPromoDiscounts[discountKey]
                  }
               })
            } else {
               // Family key is set — clear boolean flags for all members except the selected one
               familyToDiscounts[familyName].forEach((discountKey) => {
                  const discount = state.discount_list[discountKey]
                  if (discount?.DiscountCode !== selectedDiscountCode) {
                     delete cleanedPromoDiscounts[discountKey]
                  }
               })
            }
         })

         // Check all number discount values for zero or missing values
         Object.keys(validatedDiscountValues).forEach((discountKey) => {
            const discountValue = validatedDiscountValues[discountKey]
            const discount = state.discount_list[discountKey]

            // If the value is 0, null, or undefined
            if (!discountValue || discountValue === 0) {
               // Remove the value from numberDiscountValues
               delete cleanedNumberDiscountValues[discountKey]

               // Remove the discount's boolean flag
               delete cleanedPromoDiscounts[discountKey]

               // If this discount belongs to a family, remove the family selection
               if (discount && discount.DiscountFamily) {
                  // Find and remove the family selection if this discount is selected
                  Object.keys(cleanedPromoDiscounts).forEach((key) => {
                     // If this key is a family name and points to this discount code
                     if (cleanedPromoDiscounts[key] === discount.DiscountCode) {
                        delete cleanedPromoDiscounts[key]
                     }
                  })
               }
            }
         })

         // Update the promotional discounts with validated values and cleaned families
         promo_discounts = {
            ...cleanedPromoDiscounts,
            numberDiscountValues: cleanedNumberDiscountValues,
         }

         // Update the state with validated values
         state.setPromotionalDiscounts(promo_discounts)
      }
   }

   // Ensure deselected DefaultOn discounts are explicitly saved as false
   // if (state.discount_list) {
   //   Object.keys(state.discount_list).forEach(discountKey => {
   //     const discount = state.discount_list[discountKey as keyof typeof state.discount_list];
   //     if (discount &&
   //         typeof discount === 'object' &&
   //         'DefaultOn' in discount &&
   //         discount.DefaultOn &&
   //         'InputType' in discount &&
   //         discount.InputType === "Checkbox" &&
   //         !promo_discounts.hasOwnProperty(discountKey)) {
   //       // If a DefaultOn discount is not in promotional_discounts, it means it was deselected
   //       // Explicitly set it to false to save this state
   //       if (typeof promo_discounts === 'object') {
   //         (promo_discounts as Record<string, any>)[discountKey] = false;
   //       }
   //     }
   //   });
   // }
   //Set coreEECount to 0 if no core product is selected
   let coreEECount = core_count || state.productCoreEECount
   if ((core_selected || state.coreProductSelected) === 'core0') {
      state.setProductCoreEECount(0)
      coreEECount = 0
   }

   let EINNames: string[] = []
   let EECounts: number[] = []
   let StateCounts: number[] = []
   let ImpTypes: string[] = []
   let EINCount = 0
   let EINIntegrations: EINIntegrationSelection[][] = []
   /**
    * Determines if a specific EIN entry in the payroll data is considered empty.
    *
    * An EIN entry is considered empty if:
    * - The EIN name is an empty string.
    * - The employee count (EE count) is zero.
    * - The state count is less than or equal to one.
    * - There are no valid integrations, i.e., all integrations have
    *   their type or solution code set to "None".
    *
    * @param {number} row - The index of the EIN entry to check.
    * @returns {boolean} - Returns true if the entry is empty, false otherwise.
    */
   const isEmptyEINEntry = (row: number) => {
      return (
         (state?.productPayrollEINNames[row] || '') === '' &&
         (state?.productPayrollEECounts[row] || 0) === 0 &&
         (state?.productPayrollStateCounts[row] || 0) <= 1 &&
         ((state?.productEINIntegrations[row] || [])?.filter(
            (integration: EINIntegrationSelection) =>
               integration.integrationType !== 'None' &&
               integration.solutionCode !== 'None',
         )?.length || 0) === 0
      )
   }

   /**
    * Process and filter EIN (Employer Identification Number) data for payroll.
    *
    * This loop iterates through all EIN entries in the state and:
    * 1. Skips any entries that are considered "empty" based on the isEmptyEINEntry criteria
    * 2. For valid entries:
    *    - Collects the EIN name (with empty string fallback)
    *    - Collects the employee count (with 0 fallback)
    *    - Collects the state count (with 0 fallback)
    *    - Collects the implementation type (with "none" fallback)
    *    - Increments the total count of valid EINs
    *    - Filters out any integrations with "None" type or solution code
    *    - Adds the valid integrations to the EINIntegrations array
    *
    * The resulting arrays (EINNames, EECounts, StateCounts, ImpTypes, EINIntegrations)
    * will only contain data from valid EIN entries, effectively cleaning the data
    * before it is used to update the UI or sent to the backend.
    */
   for (let row = 0; row < state.productPayrollEINCount; row++) {
      if (isEmptyEINEntry(row)) {
         continue
      } else {
         EINNames.push(state.productPayrollEINNames[row] || '')
         EECounts.push(state.productPayrollEECounts[row] || 0)
         StateCounts.push(state.productPayrollStateCounts[row] || 0)
         ImpTypes.push(state.productPayrollEINImpTypes[row] || 'full')
         EINCount++
         let integrationsArray: EINIntegrationSelection[] =
            (state.productEINIntegrations[row] || [])?.filter(
               (integration: EINIntegrationSelection) =>
                  integration.integrationType !== 'None' &&
                  integration.solutionCode !== 'None',
            ) ?? []
         EINIntegrations.push(integrationsArray)
      }
   }

   // Updates UI with new values
   state.setProductPayrollEINNames(
      (ein_names?.length ?? 0 > 0) ? ein_names : EINNames,
   )
   state.setProductPayrollEECounts(
      (ein_ee_counts?.length ?? 0 > 0) ? ein_ee_counts : EECounts,
   )
   state.setProductPayrollStateCounts(
      (ein_state_counts?.length ?? 0 > 0) ? ein_state_counts : StateCounts,
   )
   state.setProductPayrollEINImpTypes(
      (ein_imp_types?.length ?? 0 > 0) ? ein_imp_types : ImpTypes,
   )
   state.setProductPayrollEINCount(ein_count || EINCount)
   state.setProductEINIntegrations(
      (ein_integrations?.length ?? 0 > 0) ? ein_integrations : EINIntegrations,
   )

   // Ensure ein_imp_types[0] and payroll_imp_type are in sync
   // If ein_imp_types[0] exists and is not null/empty, it overrides payroll_imp_type
   const finalEinImpTypes =
      (ein_imp_types?.length ?? 0 > 0) ? ein_imp_types : ImpTypes
   const finalPayrollImpType =
      finalEinImpTypes.length > 0 && finalEinImpTypes[0]
         ? finalEinImpTypes[0]
         : payroll_imp_type || state.payrollImpType
   // Set the Prepayment Activation Use Difference to false if there is no prepayment selected
   const effectivePrepayLength =
      prepay_length > 1 ? prepay_length : state.prepayLength
   if (effectivePrepayLength <= 1) {
      state.setPrepaymentActivationUseDifference(false)
      prepayment_activation_use_difference = false
   }

   // Interates through time_clock_details and removes entries with count 0
   time_clock_details = state.productTimeClockDetails.filter(
      (detail) => detail.count > 0,
   )

   // Remove entries with 0 count for consulting and professional services
   //TODO: Remove this when App.jsx is converted to typescript
   //@ts-ignore
   consulting_service_details = state.consultingServiceDetails.filter(
      (detail) => detail.count > 0,
   )
   //@ts-ignore
   professional_service_details = state.professionalServiceDetails.filter(
      (detail) => detail.count > 0,
   )

   return saveOpportunityToBackend(
      state.opportunity_id,
      state.parent_opportunity_id,
      state.account_name,
      state.opportunity_name,
      state.last_agent_email,
      state.pricebookId,
      quote_type || state.quoteType,
      expiration_date || state.quoteExpirationDate,
      core_selected || state.coreProductSelected,
      time_product_selected || state.timeProductSelected,
      coreEECount,
      productCodeToVar['performanceMan']['value'],
      time_tracking_count || state.productTimeTrackingEECount,
      has_time_clocks || state.productHasTimeClocks,
      (time_clock_details?.length ?? 0 > 0)
         ? time_clock_details
         : state.productTimeClockDetails,
      us_employees || state.hasUSEmployees,
      is_ats_only || state.isATSOnly,
      benefits_administration_count ||
         state.productBenefitsAdministrationEECount,
      payroll_count || state.productPayrollTotalEECount,
      states_count || state.productPayrollTotalStateCount,
      imp_type || state.impType,
      ben_admin_imp_type || state.benAdminImpType,
      finalPayrollImpType, //  previously payroll_imp_type. Changed with imp type selector per EIN for compatatibility.
      bundle_discount || state.discountBundle,
      nonprofit_discount || state.discountNonProfit,
      term_length > 1 ? term_length : state.termLength,
      prepay_length > 1 ? prepay_length : state.prepayLength,
      annual_prepayment_discount || state.discountPrepaymentYears,
      multi_entity_discount || state.discountMultiEntity,
      hr_promotion_monthly_discount || state.discountHRMonthlyPromotion,
      hr_dealdesk_monthly_discount || state.discountHRDealDeskMonthly,
      reseller_discount || state.discountReseller,
      quickstart_training_discount || state.discountQuickstartTraining,
      promotion_imp_discount || state.discountImpPromotion,
      hr_dealdesk_imp_discount || state.discountDealDeskHRImp,
      payroll_dealdesk_imp_discount || state.discountDealDeskPayrollImp,
      second_ein_discount || state.discountSecondEIN,
      group_imp_discount || state.discountGroupImp,
      additional_storage_count || state.productAdditionalStorageCount,
      additional_job_count || state.productAdditionalJobsCount,
      historical_employee_import || state.productHistoricalDataImport,
      marketplace_listing || state.productMarketplaceListing,
      sponsorship || state.productSponsorship,
      paid_service || state.productPaidServiceAmount,
      btoa(quote_notes || state.quoteNotes),
      test_account || productCodeToVar['testAccount']['value'], //TODO: testAccount pulling from opp isn't right, shows checked even when not
      activation_date || state.activationDate,
      (
         state.differenceQuote
            ? state.productPayrollEINCount <=
              (state.accountSnowflake?.EINs__c?.[0]?.length ?? 0)
            : state.productPayrollEINCount <= 0
      )
         ? null
         : payroll_first_pay_date !== null
           ? payroll_first_pay_date
           : state.payrollFirstPayDate,
      difference_quote || state.differenceQuote,
      is_co_term !== undefined ? is_co_term : state.isCoTerm,
      account_snowflake || state.accountSnowflake,
      core_country_count || state.coreCountryCount,
      state.PromoUTM,
      promotional_discounts || promo_discounts,
      state.accountFlags,
      next_invoice_date || state.nextInvoiceDate,
      currency_code || state.currencyCode,
      account_balance || state.accountBalance,
      monthly_tax || state.monthlyTax,
      (ein_names?.length ?? 0 > 0) ? ein_names : EINNames,
      /**
       * An array of employee counts for each EIN. The array index corresponds with the index of the EIN names in ein_names.
       * If the ein_ee_counts parameter is not provided, the value of EECounts will be used instead.
       */
      (ein_ee_counts?.length ?? 0 > 0) ? ein_ee_counts : EECounts,
      (ein_state_counts?.length ?? 0 > 0) ? ein_state_counts : StateCounts,
      (ein_imp_types?.length ?? 0 > 0) ? ein_imp_types : ImpTypes,
      ein_count || EINCount,
      (ein_integrations?.length ?? 0 > 0) ? ein_integrations : EINIntegrations,
      account_status || state.accountStatus,
      record_type || state.recordType,
      deal_desk_locks || state.dealDeskLocks,
      (professional_service_details?.length ?? 0) > 0
         ? professional_service_details
         : state.professionalServiceDetails,
      (consulting_service_details?.length ?? 0) > 0
         ? consulting_service_details
         : state.consultingServiceDetails,
      partner_account || state.partnerAccount,
      region || state.region,
      prepayment_activation_use_difference ??
         state.prepaymentActivationUseDifference,
      dynamic_products.size > 0 ? dynamic_products : state.dynamicProducts,
      stage || state.stage,
      company_id || state.companyId,
      deal_desk_discounts !== undefined
         ? deal_desk_discounts
         : state.dealDeskDiscountRecords,
   )
}

export async function saveOpportunityToBackend(
   opportunity_id: string,
   parent_opportunity_id = '',
   account_name = '',
   opportunity_name = '',
   last_agent_email = '',
   book_id = 200,
   quote_type = '',
   expiration_date = '',
   core_selected = '',
   time_product_selected = '',
   core_count = 0,
   performance_management_count = 0,
   time_tracking_count = 0,
   has_time_clocks = false,
   time_clock_details: timeClockDetail[] = [],
   us_employees = false,
   is_ats_only = false,
   benefits_administration_count = 0,
   payroll_count = 0,
   states_count = 0,
   imp_type = '',
   ben_admin_imp_type = '',
   payroll_imp_type = '',
   bundle_discount = false,
   nonprofit_discount = false,
   term_length = 1,
   prepay_length = 1,
   annual_prepayment_discount = '',
   multi_entity_discount = false,
   hr_promotion_monthly_discount = false,
   hr_dealdesk_monthly_discount = false,
   reseller_discount = 0,
   quickstart_training_discount = false,
   promotion_imp_discount = false,
   hr_dealdesk_imp_discount = false,
   payroll_dealdesk_imp_discount = false,
   second_ein_discount = false,
   group_imp_discount = false,
   additional_storage_count = 0,
   additional_job_count = 0,
   historical_employee_import = false,
   marketplace_listing = false,
   sponsorship = false,
   paid_service = 0,
   quote_notes = '',
   test_account = false,
   activation_date = '',
   payroll_first_pay_date: string | null = null,
   difference_quote = false,
   is_co_term = false,
   account_snowflake = '',
   core_country_count = 0,
   promo_utm = '',
   promotional_discounts = {},
   account_flags = '',
   next_invoice_date = '',
   currency_code: string,
   account_balance = 0,
   monthly_tax = 0,
   ein_names: string[] = [],
   ein_ee_counts: number[] = [],
   ein_state_counts: number[] = [],
   ein_imp_types: string[] = [],
   ein_count = 0,
   ein_integrations: EINIntegrationSelection[][] = [],
   account_status = '',
   record_type = '',
   deal_desk_locks: Set<string> = new Set<string>(),
   professional_service_details: {
      serviceCode: string
      count: number
      [key: string]: string | number
   }[] = [],
   consulting_service_details: {
      serviceCode: string
      count: number
      [key: string]: string | number
   }[] = [],
   partner_account = '',
   region = '',
   prepayment_activation_use_difference = false,
   dynamic_products: Map<string, number> = new Map<string, number>(),
   stage = '',
   company_id = '',
   deal_desk_discounts: DealDeskDiscount[] = [],
) {
   if (opportunity_id) {
      // Create parameter object for the request.
      let parametersObject = {
         opportunity_id: opportunity_id,
         parent_opportunity_id: parent_opportunity_id,
         account_name: account_name,
         opportunity_name: opportunity_name,
         last_agent_email: last_agent_email,
         book_id: book_id,
         expiration_date: expiration_date,
         core_selected: core_selected,
         time_product_selected: time_product_selected,
         core_count: core_count,
         performance_management_count: performance_management_count,
         time_tracking_count: time_tracking_count,
         has_time_clocks: has_time_clocks,
         time_clock_details: time_clock_details,
         us_employees: us_employees,
         is_ats_only: is_ats_only,
         benefits_administration_count: benefits_administration_count,
         payroll_count: payroll_count,
         states_count: states_count,
         imp_type: imp_type,
         ben_admin_imp_type: ben_admin_imp_type,
         payroll_imp_type: payroll_imp_type,
         bundle_discount: bundle_discount,
         nonprofit_discount: nonprofit_discount,
         term_length: term_length,
         prepay_length: prepay_length,
         annual_prepayment_discount: annual_prepayment_discount,
         multi_entity_discount: multi_entity_discount,
         hr_promotion_monthly_discount: hr_promotion_monthly_discount,
         hr_dealdesk_monthly_discount: hr_dealdesk_monthly_discount,
         reseller_discount: reseller_discount,
         quickstart_training_discount: quickstart_training_discount,
         promotion_imp_discount: promotion_imp_discount,
         hr_dealdesk_imp_discount: hr_dealdesk_imp_discount,
         payroll_dealdesk_imp_discount: payroll_dealdesk_imp_discount,
         second_ein_discount: second_ein_discount,
         group_imp_discount: group_imp_discount,
         additional_storage_count: additional_storage_count,
         additional_job_count: additional_job_count,
         historical_employee_import: historical_employee_import,
         marketplace_listing: marketplace_listing,
         sponsorship: sponsorship,
         paid_service: paid_service,
         quote_notes: quote_notes,
         test_account: test_account,
         activation_date: activation_date,
         payroll_first_pay_date: payroll_first_pay_date,
         difference_quote: difference_quote,
         is_co_term: is_co_term,
         account_snowflake: account_snowflake,
         quote_type: quote_type,
         core_country_count: core_country_count,
         promo_utm: promo_utm,
         promotional_discounts: promotional_discounts,
         account_flags: account_flags,
         next_invoice_date: next_invoice_date,
         currency_code: currency_code,
         account_balance: account_balance,
         monthly_tax: monthly_tax,
         ein_names: ein_names,
         ein_ee_counts: ein_ee_counts,
         ein_state_counts: ein_state_counts,
         ein_imp_types: ein_imp_types,
         ein_count: ein_count,
         ein_integrations: ein_integrations,
         account_status: account_status,
         record_type: record_type,
         deal_desk_locks: Array.from(deal_desk_locks),
         professional_service_details: professional_service_details,
         consulting_service_details: consulting_service_details,
         partner_account: partner_account,
         region: region,
         prepayment_activation_use_difference:
            prepayment_activation_use_difference,
         stage: stage,
         company_id: company_id,
      }

      // First save the opportunity and wait for it to complete
      return fetch(`${host_url}/api/save-opp`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
         },
         body: JSON.stringify(parametersObject),
      }).then((opportunityResponse) => {
         // Check if the opportunity save was successful
         if (!opportunityResponse.ok) {
            throw new Error(
               `Failed to save opportunity: ${opportunityResponse.status}`,
            )
         }

         // Chain promises for dynamic products and deal desk discounts
         let saveChain = Promise.resolve(opportunityResponse)

         // Save dynamic products if there are any
         if (dynamic_products.size > 0) {
            const dynamic_products_encoded = Array.from(
               dynamic_products?.entries() ?? [],
            )

            saveChain = saveChain.then(() =>
               fetch(`${host_url}/api/opportunity-products`, {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                     opportunity_id,
                     products: dynamic_products_encoded,
                  }),
               }).then((productsResponse) => {
                  if (!productsResponse.ok) {
                     throw new Error(
                        `Failed to save products: ${productsResponse.status}`,
                     )
                  }
                  return opportunityResponse
               }),
            )
         }

         // Save deal desk discounts (including empty array to delete all)
         // apply_to is already an array in snake_case format
         const discountsForAPI = deal_desk_discounts

         saveChain = saveChain.then(() =>
            fetch(`${host_url}/api/dealdesk-discount`, {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  opportunity_id,
                  discounts: discountsForAPI,
               }),
            }).then(async (discountsResponse) => {
               if (!discountsResponse.ok) {
                  // Try to parse the error response for validation details
                  try {
                     const errorData = await discountsResponse.json()
                     if (
                        errorData.validation_errors &&
                        errorData.validation_errors.length > 0
                     ) {
                        throw new Error(errorData.validation_errors.join('\n'))
                     }
                     throw new Error(
                        errorData.message ||
                           errorData.error ||
                           `Failed to save deal desk discounts: ${discountsResponse.status}`,
                     )
                  } catch (parseError) {
                     // If parsing fails, check if it's already our error
                     if (
                        parseError instanceof Error &&
                        parseError.message !== 'Unexpected end of JSON input'
                     ) {
                        throw parseError
                     }
                     throw new Error(
                        `Failed to save deal desk discounts: ${discountsResponse.status}`,
                     )
                  }
               }
               return opportunityResponse
            }),
         )

         return saveChain
      })
   } else {
      throw new Error(
         `Invalid Opportunity ID: ${opportunity_id}\nUnable to Save.`,
      )
   }
}

export const useData = (url: string) => {
   const [state, setState] = useState({})

   useEffect(() => {
      const dataFetch = async () => {
         const data = await (await fetch(url)).json()

         setState(data)
      }

      dataFetch()
   }, [url])

   return { data: state }
}
